import { Fetcher, dig } from '@this/src/util';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@this/shared/ui/data_displays/table';
import { Select } from '@this/shared/ui/inputs/select';
import type { SuppliedItemArgs } from '@this/domain/supplied_item/supplied_item';
import SuppliedItem from '@this/domain/supplied_item/supplied_item';
import type { IndividualRailwaySuppliedItemArgs } from '@this/domain/individual_railway_supplied_item/individual_railway_supplied_item';
import IndividualRailwaySuppliedItem from '@this/domain/individual_railway_supplied_item/individual_railway_supplied_item';
import { Button } from '../../../shared/ui/inputs/button';

interface Response {
  item_mappings: IndividualRailwaySuppliedItemArgs[];
  supplied_items: SuppliedItemArgs[];
}

const TicketingMethodMapping = () => {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [itemMappings, setItemMappings] = useState<IndividualRailwaySuppliedItem[]>([]);
  const [suppliedItems, setSuppliedItems] = useState<SuppliedItem[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchItemMappings = () => {
    setLoading(true);
    Fetcher.get<Response>('/admin/individual_railway_supplied_items.json')
      .then(res => {
        setItemMappings(res.item_mappings.map(mapping => new IndividualRailwaySuppliedItem(mapping)));
        setSuppliedItems(res.supplied_items.map(item => new SuppliedItem(item)));
      })
      .catch(e => {
        setErrorMessage(
          dig(e, 'responseJSON', 'message') || '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const saveMapping = (mapping: IndividualRailwaySuppliedItem) => {
    setSaving(true);
    Fetcher.put(`/admin/individual_railway_supplied_items/${mapping.id}.json`, mapping.submitParams())
      .then(() => {
        setSuccessMessage('保存しました。');
        setErrorMessage(null);
      })
      .catch(e => {
        setErrorMessage(
          dig(e, 'responseJSON', 'message') || '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
        );
        setSuccessMessage(null);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  useEffect(() => {
    fetchItemMappings();
  }, []);

  return (
    <>
      {errorMessage && (
        <Alert variant="danger" onClose={() => setErrorMessage(null)} style={{ marginBottom: '16px' }}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage(null)} style={{ marginBottom: '16px' }}>
          {successMessage}
        </Alert>
      )}
      {loading ? (
        <Loading />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>発券方法</TableCell>
                <TableCell>仕入商品</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {itemMappings.map(mapping => (
                <TableRow key={mapping.id}>
                  <TableCell>{mapping.ticketingMethodText}</TableCell>
                  <TableCell>
                    <Select
                      value={mapping.suppliedItemId?.toString() || ''}
                      onChange={e => {
                        mapping.suppliedItemId = parseInt(e.target.value, 10) || null;
                      }}
                    >
                      <option value="">選択してください</option>
                      {suppliedItems.map(item => (
                        <option key={item.id} value={item.id.toString()}>
                          {item.supplierName}：{item.name}
                        </option>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button onClick={() => saveMapping(mapping)} disabled={saving}>
                      保存
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default observer(TicketingMethodMapping);
